import { useEffect, useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import { api } from '@/utils/api';

export interface Vetting {
  verdict: string;
  score: number;
  reasons: string[];
  risks: string[];
  summary: string;
  source: 'ai' | 'fallback';
}

interface CreatorAiModalProps {
  /** Which of the live creator page's AI cards: "AI fit summary" (vet) or "Outreach draft". */
  mode: 'vet' | 'outreach' | null;
  campaignId: string;
  rosterId: string;
  creatorName: string;
  onClose: () => void;
  /** Called with each vetting result, so the roster's Fit column can show it. */
  onVetted: (rosterId: string, result: Vetting) => void;
}

/**
 * POST campaigns/{id}/creators/{cc}/vet and GET …/outreach-draft. Each runs when the
 * modal opens and again on "Run again"; the draft can be edited and copied.
 */
export default function CreatorAiModal({ mode, campaignId, rosterId, creatorName, onClose, onVetted }: CreatorAiModalProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [vetting, setVetting] = useState<Vetting | null>(null);
  const [draft, setDraft] = useState('');
  const [copied, setCopied] = useState(false);

  const run = async () => {
    if (!mode) return;
    setBusy(true);
    setError('');
    setCopied(false);
    const base = `/campaigns/${campaignId}/creators/${rosterId}`;
    try {
      if (mode === 'vet') {
        const result = await api<Vetting>(`${base}/vet`, { method: 'POST' });
        setVetting(result);
        onVetted(rosterId, result);
      } else {
        setDraft((await api<{ draft: string }>(`${base}/outreach-draft`)).draft);
      }
    } catch (runError) {
      setError(runError instanceof Error ? runError.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setVetting(null);
    setDraft('');
    setError('');
    if (mode) void run();
    // Runs once per opening (mode or creator change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, rosterId]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Modal
      open={mode !== null}
      onClose={onClose}
      icon={mode === 'vet' ? 'ri-sparkling-2-line' : 'ri-quill-pen-line'}
      title={mode === 'vet' ? `AI fit summary: ${creatorName}` : `Outreach draft for ${creatorName}`}
      subtitle={mode === 'vet' ? 'How well this creator fits the campaign brief' : 'A personalised message to send with the brief'}
      footer={
        <div className="flex justify-end gap-2">
          {mode === 'outreach' && draft ? (
            <Button variant="outline" icon={copied ? 'ri-check-line' : 'ri-file-copy-line'} onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          ) : null}
          <Button variant="outline" icon="ri-refresh-line" disabled={busy} onClick={() => void run()}>
            Run again
          </Button>
          <Button onClick={onClose}>Done</Button>
        </div>
      }
    >
      {busy ? (
        <p className="flex items-center gap-2 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin" />
          {mode === 'vet' ? 'Assessing…' : 'Drafting…'}
        </p>
      ) : null}
      {error && !busy ? (
        <p role="alert" className="flex items-start gap-1.5 text-sm text-primary-700">
          <i className="ri-error-warning-line mt-0.5" />
          {error}
        </p>
      ) : null}

      {mode === 'vet' && vetting && !busy ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-lg bg-accent-100 text-accent-800">
              <span className="font-heading text-2xl font-bold leading-none">{vetting.score > 0 ? `${vetting.score}/5` : '—'}</span>
              <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide">fit</span>
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground-900">{vetting.verdict}</p>
              <p className="mt-1 text-sm leading-relaxed text-foreground-600">{vetting.summary}</p>
            </div>
          </div>
          {vetting.reasons.length > 0 || vetting.risks.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 border-t border-background-200 pt-3 md:grid-cols-2">
              {vetting.reasons.length > 0 ? (
                <div>
                  <p className="text-[11px] font-label font-semibold uppercase tracking-wider text-accent-700">Why it fits</p>
                  <ul className="mt-1.5 space-y-1 text-sm text-foreground-700">
                    {vetting.reasons.map((reason) => (
                      <li key={reason} className="flex gap-1.5"><i className="ri-check-line mt-0.5 text-accent-600" />{reason}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {vetting.risks.length > 0 ? (
                <div>
                  <p className="text-[11px] font-label font-semibold uppercase tracking-wider text-primary-700">Risks</p>
                  <ul className="mt-1.5 space-y-1 text-sm text-foreground-700">
                    {vetting.risks.map((risk) => (
                      <li key={risk} className="flex gap-1.5"><i className="ri-alert-line mt-0.5 text-primary-600" />{risk}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {mode === 'outreach' && draft && !busy ? (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(event) => { setDraft(event.target.value); setCopied(false); }}
            rows={5}
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
          />
          <p className="text-[11px] text-foreground-400">Edit it as you like, then copy it into your message to the creator.</p>
        </div>
      ) : null}
    </Modal>
  );
}
